/**
 * This is the entry point for Feature Hub App integration
 */

import React from 'react';
import type {
  FeatureAppDefinition,
  FeatureAppEnvironment,
  FeatureServices,
} from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import { I18nextProvider } from 'react-i18next';
import { Provider as StoreProvider } from 'react-redux';
import type { Logger } from '@feature-hub/logger';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import type { GfaLocaleServiceV1 } from '@volkswagen-onehub/gfa-locale-service';
import type { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import type { TrackingServiceV2 } from '@oneaudi/audi-tracking-service';
import App from './FeatureApp';
import { getDesignLibrary, getThemeName } from '../alpha-helper';
import { store } from './redux';
import i18n from '../i18n';

export interface FeatureServiceDependencies extends FeatureServices {
  readonly 's2:logger'?: Logger;
  readonly 'gfa:locale-service': GfaLocaleServiceV1;
  readonly 'audi-tracking-service'?: TrackingServiceV2;
}

const normalizeConfig = (config: any) => {
  console.log(config);
  if (config?.__type === 'aem-headless') {
    return config?.config?.fields;
  }

  return config;
};

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      's2:logger': '^1.0.0',
      'gfa:locale-service': '^1.0.0',
    },
    externals: {
      react: '*',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '^1.8.0' }),
    },
  },

  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
    },
  },

  create: ({
    featureServices,
    config: remoteConfig,
  }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const loggerService = featureServices['s2:logger'];
    loggerService?.info('Feature App created.');

    const trackingService = featureServices['audi-tracking-service'];

    if (trackingService) {
      trackingService.featureAppName = __FEATURE_APP_NAME__;
    }

    const configProvider = featureServices[
      'gfa:service-config-provider'
    ] as GfaServiceConfigProviderV1;

    const normalConfig = normalizeConfig(remoteConfig);

    return {
      render: () => {
        return (
          <ThemeProvider theme={{ name: getThemeName() }}>
            <StoreProvider store={store}>
              <I18nextProvider i18n={i18n}>
                <App
                  configProvider={configProvider}
                  remoteConfig={normalConfig}
                  trackingService={trackingService}
                />
              </I18nextProvider>
            </StoreProvider>
          </ThemeProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
